import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";

const GROUP_ICON = { clients: "C", providers: "P", assignments: "A", evaluations: "E" };

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (event.key === "Escape") setOpen(false);
    };
    const onClick = (event) => { if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false); };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { window.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResult(null); setLoading(false); return undefined; }
    setLoading(true);
    let active = true;
    const timer = setTimeout(() => {
      api.get("/search", { params: { q } })
        .then((r) => { if (active) { setResult(r.data); setError(""); } })
        .catch(() => { if (active) setError("Search is unavailable right now."); })
        .finally(() => { if (active) setLoading(false); });
    }, 220);
    return () => { active = false; clearTimeout(timer); };
  }, [query]);

  const go = (href) => { setOpen(false); setQuery(""); setResult(null); navigate(href); };
  const onSubmit = async (event) => {
    event.preventDefault();
    const q = query.trim();
    if (q.length < 2) { setOpen(true); return; }
    setOpen(true);
    let data = result?.query === q ? result : null;
    if (!data) {
      setLoading(true);
      try {
        data = (await api.get("/search", { params: { q } })).data;
        setResult(data);
        setError("");
      } catch {
        setError("Search is unavailable right now.");
      } finally {
        setLoading(false);
      }
    }
    const first = data?.groups?.[0]?.items?.[0];
    if (first) go(first.href);
  };
  const showPanel = open && query.trim().length >= 1;

  return (
    <div className="header-search" ref={boxRef} data-testid="global-search">
      <form onSubmit={onSubmit} className="header-search-form" role="search">
        <Search size={18} aria-hidden="true" />
        <input
          ref={inputRef}
          data-testid="global-search-input"
          placeholder="Search clients, providers, assignments, evaluations…"
          aria-label="Search TAGForce"
          value={query}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        {query ? <button type="button" className="header-search-clear" aria-label="Clear search" data-testid="global-search-clear" onClick={() => { setQuery(""); setResult(null); inputRef.current?.focus(); }}><X size={14} /></button> : <kbd data-testid="global-search-shortcut">⌘ K</kbd>}
      </form>
      {showPanel && (
        <div className={`search-panel${loading ? " is-loading" : ""}`} data-testid="global-search-panel" role="listbox" aria-busy={loading}>
          {query.trim().length < 2 && <p className="search-hint" data-testid="global-search-hint">Type at least 2 characters.</p>}
          {query.trim().length >= 2 && loading && <p className="search-hint" data-testid="global-search-loading">Searching…</p>}
          {error && <p className="search-hint error" data-testid="global-search-error">{error}</p>}
          {result && result.total === 0 && !loading && <p className="search-empty" data-testid="global-search-empty">No results for “{result.query}”. Try a name, school, request ID or evaluation type.</p>}
          {result?.groups?.map((group) => (
            <section key={group.key} className="search-group" data-testid={`global-search-group-${group.key}`}>
              <h4><span className="search-group-badge" aria-hidden="true">{GROUP_ICON[group.key]}</span>{group.label} <small>{group.count}</small></h4>
              <ul>
                {group.items.map((item) => (
                  <li key={item.id}>
                    <button type="button" className="search-result" data-testid={`global-search-result-${item.id}`} onClick={() => go(item.href)}>
                      <strong>{item.title}</strong>
                      {item.subtitle && <small>{item.subtitle}</small>}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {result && result.total > 0 && <p className="search-hint" data-testid="global-search-enter-hint">Press Enter to open the first result.</p>}
        </div>
      )}
    </div>
  );
};
